import { authFetch } from "./authApi";

const BILLING_STATUS_CACHE_KEY = "billingStatusesCache";
const BILLING_STATUS_CACHE_TTL_MS = 5 * 60 * 1000;

let billingStatusInflightPromise = null;
let billingStatusMemoryCache = {
  loadedAt: 0,
  payload: null,
};

async function requestJson(path, options = {}) {
  const response = await authFetch(path, options);
  if (!response.ok) {
    const body = await response.text();
    throw new Error(`API ${response.status} on ${path}: ${body || response.statusText}`);
  }
  return response.json();
}

export async function fetchOutstandingInvoices() {
  return requestJson("/oauth/invoices/outstanding");
}

function normalizeBillingPayload(payload) {
  return {
    count: Number(payload?.count || 0),
    generated_at: String(payload?.generated_at || ""),
    statuses: Array.isArray(payload?.statuses) ? payload.statuses : [],
    source: payload?.source || "",
    stale: Boolean(payload?.stale),
    warning: String(payload?.warning || ""),
  };
}

function readBillingStatusCache() {
  try {
    const raw = window.localStorage.getItem(BILLING_STATUS_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const loadedAt = Number(parsed?.loadedAt || 0);
    const payload = normalizeBillingPayload(parsed?.payload || {});
    if (!loadedAt || !Array.isArray(payload.statuses)) return null;
    return { loadedAt, payload };
  } catch {
    return null;
  }
}

function writeBillingStatusCache(payload) {
  try {
    const next = {
      loadedAt: Date.now(),
      payload: normalizeBillingPayload(payload),
    };
    window.localStorage.setItem(BILLING_STATUS_CACHE_KEY, JSON.stringify(next));
    billingStatusMemoryCache = next;
  } catch {
    // Ignore storage failures.
  }
}

export async function fetchBillingStatuses() {
  const now = Date.now();

  if (
    billingStatusMemoryCache.payload &&
    now - Number(billingStatusMemoryCache.loadedAt || 0) <= BILLING_STATUS_CACHE_TTL_MS
  ) {
    return billingStatusMemoryCache.payload;
  }

  const stored = readBillingStatusCache();
  if (stored) {
    billingStatusMemoryCache = stored;
    if (now - stored.loadedAt <= BILLING_STATUS_CACHE_TTL_MS) {
      return stored.payload;
    }
  }

  if (billingStatusInflightPromise) {
    return billingStatusInflightPromise;
  }

  billingStatusInflightPromise = requestJson("/oauth/billing/statuses")
    .then((payload) => {
      const normalized = normalizeBillingPayload(payload);
      writeBillingStatusCache(normalized);
      return normalized;
    })
    .catch((error) => {
      const fallback = readBillingStatusCache();
      if (fallback?.payload) {
        billingStatusMemoryCache = fallback;
        return fallback.payload;
      }
      throw error;
    })
    .finally(() => {
      billingStatusInflightPromise = null;
    });

  return billingStatusInflightPromise;
}
